import type { FC } from 'react';
import React, { useMemo } from 'react';
import type { PliantTransaction } from '@pflegenavi/frontend/api-nursing-home';
import { PliantStatus } from '@pflegenavi/frontend/api-nursing-home';

import { useTranslation } from 'react-i18next';

import type { LabelColor, UrlFilters } from '@pflegenavi/web-components';
import {
  type ConfigType,
  Label,
  makeConfig,
  PaginatedTable2,
  TruncatedTooltipText,
  Typography,
  urlDateRange,
  urlString,
} from '@pflegenavi/web-components';
import { Box, Stack } from '@mui/material';
import {
  TIME_FORMAT,
  YEAR_SHORTMONTH_DAY_FORMAT,
} from '@pflegenavi/shared/constants';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';

export enum PliantTransactionTabValues {
  All = 'all',
  Completed = 'completed',
  Declined = 'declined',
}

export const TAB_STATUSES: Record<PliantTransactionTabValues, PliantStatus[]> =
  {
    [PliantTransactionTabValues.All]: [
      PliantStatus.CONFIRMED,
      PliantStatus.BOOKED,
      PliantStatus.PENDING,
      PliantStatus.DECLINED,
      PliantStatus.REVERSED,
    ],
    [PliantTransactionTabValues.Completed]: [
      PliantStatus.CONFIRMED,
      PliantStatus.BOOKED,
      PliantStatus.PENDING,
    ],
    [PliantTransactionTabValues.Declined]: [
      PliantStatus.DECLINED,
      PliantStatus.REVERSED,
    ],
  };

/** Maps the raw URL value to a tab, falling back to "all" for anything unknown. */
export const readPliantTransactionTab = (
  value: string | undefined
): PliantTransactionTabValues =>
  Object.values(PliantTransactionTabValues).find((tab) => tab === value) ??
  PliantTransactionTabValues.All;

// Tab, filters, page and page size live in the URL so the view survives
// navigating away and back.
export const PLIANT_TRANSACTION_FILTER_CODECS = {
  tab: urlString('tab'),
  createdAt: urlDateRange('createdFrom', 'createdTo'),
  bookedAt: urlDateRange('bookedFrom', 'bookedTo'),
};
type PliantTransactionFilters = UrlFilters<
  typeof PLIANT_TRANSACTION_FILTER_CODECS
>;

interface PliantTransactionsTableProps {
  isLoading: boolean;
  transactions: PliantTransaction[] | undefined;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  filters: PliantTransactionFilters;
}

interface PliantTransactionTableRow {
  id: string;
  cardholderName: string;
  merchantName: string;
  createdAt: string;
  bookedAt: string;
  amount: number;
  typeColor: LabelColor;
  typeString: string;
  statusColor: LabelColor;
  statusString: string;
  status: PliantStatus;
}

type ColumnNames =
  | 'cardholder'
  | 'merchantName'
  | 'createdAt'
  | 'bookedAt'
  | 'amount'
  | 'type'
  | 'status';

type Filters = 'createdAt' | 'bookedAt';

export const PliantTransactionsTable: FC<PliantTransactionsTableProps> = ({
  isLoading,
  transactions,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  filters: { values: filterValues, set: setFilter },
}) => {
  const { t } = useTranslation();
  const fDate = useFormatDate();
  const { fCurrencyCents } = useFormatting();
  const tab = readPliantTransactionTab(filterValues.tab);

  const rows = useMemo<PliantTransactionTableRow[]>(
    () =>
      transactions?.map((transaction) => {
        const statusColor = [
          PliantStatus.CONFIRMED,
          PliantStatus.BOOKED,
          PliantStatus.PENDING,
        ].includes(transaction.pliant_status)
          ? 'success'
          : ('error' as LabelColor);
        const statusString = [
          PliantStatus.CONFIRMED,
          PliantStatus.BOOKED,
          PliantStatus.PENDING,
        ].includes(transaction.pliant_status)
          ? 'completed'
          : 'failed';

        return {
          id: transaction.pliant_transaction_id,
          cardholderName:
            `${transaction.pliant_cardholder.employee.first_name || ''} ${
              transaction.pliant_cardholder.employee.last_name || ''
            }`.trim() || '--',
          merchantName: transaction.merchant_name || '-',
          createdAt: transaction.created_at
            ? fDate(new Date(transaction.created_at), {
                ...TIME_FORMAT,
                ...YEAR_SHORTMONTH_DAY_FORMAT,
              })
            : '--',
          bookedAt: transaction.booked_at
            ? fDate(new Date(transaction.booked_at), {
                ...TIME_FORMAT,
                ...YEAR_SHORTMONTH_DAY_FORMAT,
              })
            : '--',
          amount: transaction.billing_amount,
          typeColor:
            transaction.pliant_type === 'CASH_WITHDRAWAL' ? 'info' : 'success',
          typeString: transaction.pliant_type,
          statusColor,
          statusString,
          status: transaction.pliant_status,
        };
      }) ?? [],
    [transactions, fDate]
  );

  const config: ConfigType<PliantTransactionTableRow, ColumnNames, Filters> =
    useMemo(
      () =>
        makeConfig<PliantTransactionTableRow, ColumnNames, Filters>({
          showAll: undefined,
          onClickRow: undefined,
          hoverRow: true,
          tabs: {
            selectedItem: tab,
            updateFilterModel: (value: PliantTransactionTabValues) =>
              setFilter(
                'tab',
                value === PliantTransactionTabValues.All ? undefined : value
              ),
            items: [
              {
                value: PliantTransactionTabValues.All,
                label: t('accounting.pliant.states.all'),
                color: 'default',
                count: undefined,
              },
              {
                value: PliantTransactionTabValues.Completed,
                label: t('accounting.pliant.states.completed'),
                color: 'success',
                count: undefined,
              },
              {
                value: PliantTransactionTabValues.Declined,
                label: t('accounting.pliant.states.failed'),
                color: 'error',
                count: undefined,
              },
            ],
          },
          filters: {
            variant: 'pills',
            items: [
              {
                key: 'createdAt',
                type: 'dateRange',
                label: t('accounting.pliant.table-head.transaction-date'),
                range: filterValues.createdAt,
                onChange: (range) => setFilter('createdAt', range),
              },
              {
                key: 'bookedAt',
                type: 'dateRange',
                label: t('accounting.pliant.table-head.booked-at'),
                range: filterValues.bookedAt,
                onChange: (range) => setFilter('bookedAt', range),
              },
            ],
          },
          columns: [
            {
              key: 'cardholder',
              label: t('accounting.pliant.table-head.cardholder'),
              align: 'left',
              sx: { p: 2 },
              component: ({ row: { cardholderName } }) => (
                <Box sx={{ py: 1 }}>
                  <TruncatedTooltipText
                    text={cardholderName}
                    variant="subtitle2"
                    lineHeight={1.5}
                    maxWidth={300}
                  />
                </Box>
              ),
            },
            {
              key: 'merchantName',
              label: t('accounting.pliant.table-head.merchant'),
              align: 'left',
              maxWidth: 160,
              component: ({ row: { merchantName } }) => (
                <Typography variant="subtitle2" noWrap>
                  {merchantName}
                </Typography>
              ),
            },
            {
              key: 'createdAt',
              label: t('accounting.pliant.table-head.transaction-date'),
              align: 'left',
              component: ({ row: { createdAt } }) => (
                <Typography variant="subtitle2" noWrap>
                  {createdAt}
                </Typography>
              ),
            },
            {
              key: 'bookedAt',
              label: t('accounting.pliant.table-head.booked-at'),
              align: 'left',
              component: ({ row: { bookedAt } }) => (
                <Typography variant="subtitle2" noWrap>
                  {bookedAt}
                </Typography>
              ),
            },
            {
              key: 'amount',
              label: t('accounting.pliant.table-head.amount'),
              align: 'right',
              minWidth: 100,
              maxWidth: 150,
              sx: { paddingRight: 8 },
              component: ({ row: { amount } }) => (
                <Stack sx={{ paddingRight: 6 }}>
                  <Typography variant="subtitle2">
                    {fCurrencyCents(amount)}
                  </Typography>
                </Stack>
              ),
            },
            {
              key: 'type',
              label: t('accounting.pliant.table-head.type'),
              align: 'left',
              component: ({ row: { typeColor, typeString } }) => (
                <Label color={typeColor} variant="ghost">
                  {typeString === 'CASH_WITHDRAWAL'
                    ? t('receipts.unassociatedPliant.type.cashWithdrawal')
                    : t('receipts.unassociatedPliant.type.cardPayment')}
                </Label>
              ),
            },
            {
              key: 'status',
              label: t('accounting.pliant.table-head.status'),
              align: 'left',
              component: ({ row: { statusColor, statusString } }) => (
                <Label color={statusColor} variant="ghost">
                  {t(`accounting.pliant.states.${statusString}`)}
                </Label>
              ),
            },
          ],
          emptyRowHeight: 77,
          pagination: {
            total: totalCount,
            page,
            onPageChange,
            pageSize,
            onPageSizeChange,
            pageSizeOptions: [5, 10, 25, 50],
          },
        }),
      [
        tab,
        t,
        totalCount,
        page,
        onPageChange,
        pageSize,
        onPageSizeChange,
        fCurrencyCents,
        filterValues,
        setFilter,
      ]
    );

  return (
    <PaginatedTable2
      name="PliantTransactionsTable"
      isLoading={isLoading}
      config={config}
      rows={rows}
    />
  );
};
